/**
 * Phase 128 — "Live in the city" ambient civic-motion ticker.
 *
 * Shows the next 2 hours of city sports activity as anonymized
 * narrative summaries.  Designed to live at the top of /arenax (below
 * the hero) and as a sidebar strip on rankings / city feed / venue
 * detail pages.
 *
 * Doctrine: ambient, not transactional.  Never feels like an
 * inventory list; always feels like the city breathing.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Activity, MapPin, Loader2, IndianRupee } from 'lucide-react';

const POLL_INTERVAL_MS = 45 * 1000; // refresh every 45s

export default function ArenaxCityPulseTicker({ city = null, layout = 'carousel', maxItems = 10 }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const fetchOnce = async () => {
      try {
        const params = new URLSearchParams({ limit: String(maxItems) });
        if (city) params.set('city', city);
        const r = await api.get(`/arenax/city-pulse?${params}`);
        if (mountedRef.current) {
          setItems(r.data?.items || []);
          setSummary(r.data?.city_summary || null);
        }
      } catch (_e) { /* silent — ambient widget */ }
      finally { if (mountedRef.current) setLoading(false); }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, [city, maxItems]);

  if (loading) {
    return (
      <div className="h-14 flex items-center px-4 rounded-xl"
           style={{ background: 'var(--ax-card-bg-soft)', border: '1px solid var(--ax-border-quiet)' }}
           data-testid="arenax-city-pulse-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin"
                 style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
        <span className="ml-2 text-xs italic"
              style={{ color: 'hsl(var(--ax-text-muted))' }}>
          Reading the city's pulse…
        </span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="h-14 flex items-center justify-between px-4 rounded-xl"
           style={{ background: 'var(--ax-card-bg-soft)', border: '1px solid var(--ax-border-quiet)' }}
           data-testid="arenax-city-pulse-empty">
        <div className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" style={{ color: 'hsl(var(--ax-text-faint))' }} />
          <span className="text-xs italic" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            The city's quiet right now. Be the first to book tonight.
          </span>
        </div>
        <button onClick={() => navigate('/arenax/venues')}
                className="text-[11px] font-bold underline-offset-2 hover:underline"
                style={{ color: 'hsl(var(--ax-accent-cyan))' }}
                data-testid="city-pulse-browse-cta">
          Browse venues →
        </button>
      </div>
    );
  }

  if (layout === 'list') {
    return (
      <div className="space-y-1.5" data-testid="arenax-city-pulse-list">
        {items.map(i => <TickerItem key={i.slot_id} item={i} layout="list"
                                    onClick={() => i.venue_slug && navigate(`/arenax/venues/${i.venue_slug}`)} />)}
      </div>
    );
  }

  // Carousel — horizontal, marquee-like scroll
  return (
    <div className="relative arenax-card overflow-hidden" data-testid="arenax-city-pulse-ticker"
         style={{ borderRadius: 14 }}>
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b"
           style={{ borderColor: 'var(--ax-border-quiet)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="relative inline-flex h-2 w-2 rounded-full"
                style={{
                  background: '#10b981',
                  boxShadow: '0 0 0 0 rgba(16,185,129,0.45)',
                  animation: 'arenax-pulse-cyan 1.5s ease-in-out infinite',
                }} />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold whitespace-nowrap"
               style={{ color: 'hsl(var(--ax-text-faint))' }}>
            Live in the city · next 2 hours
          </div>
        </div>
        {summary?.label && (
          <div className="flex items-center gap-1.5 text-[10px] font-bold whitespace-nowrap pl-3 truncate"
               style={{ color: 'hsl(var(--ax-text-muted))' }}
               data-testid="arenax-city-pulse-summary">
            <span aria-hidden="true">👁</span>
            <span className="truncate">{summary.label}</span>
          </div>
        )}
      </div>
      <div className="overflow-x-auto py-2.5 px-3" data-testid="arenax-city-pulse-strip">
        <div className="flex items-center gap-2.5">
          {items.map(i => (
            <TickerItem
              key={i.slot_id}
              item={i}
              layout="chip"
              onClick={() => i.venue_slug && navigate(`/arenax/venues/${i.venue_slug}`)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TickerItem({ item, layout, onClick }) {
  const isLive = item.kind === 'live';
  const isCompleted = item.kind === 'completed';
  const accent = isLive ? '#0e7490'
                : isCompleted ? '#047857'
                : 'hsl(var(--ax-text))';
  const bg = isLive ? 'rgba(6,182,212,0.10)'
              : isCompleted ? 'rgba(16,185,129,0.08)'
              : 'var(--ax-card-bg-soft)';
  const border = isLive ? 'rgba(6,182,212,0.30)'
                 : isCompleted ? 'rgba(16,185,129,0.30)'
                 : 'var(--ax-border-quiet)';

  if (layout === 'list') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left px-3 py-2 rounded-lg flex items-center justify-between gap-3 transition-colors hover:opacity-90"
        style={{ background: bg, border: `1px solid ${border}` }}
        data-testid={`city-pulse-item-${item.slot_id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base">{item.emoji}</span>
          <div className="min-w-0">
            <div className="text-xs font-bold truncate" style={{ color: accent }}>
              {item.sport || 'Sport'} · {item.area || item.city || ''}
            </div>
            <div className="text-[10px] truncate" style={{ color: 'hsl(var(--ax-text-muted))' }}>
              {item.venue_name} · {item.court_name || ''}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold whitespace-nowrap"
               style={{ color: accent }}>
            {item.when_label}
          </div>
          {item.price && (
            <div className="text-[10px] inline-flex items-center"
                 style={{ color: 'hsl(var(--ax-text-muted))' }}>
              <IndianRupee className="h-2.5 w-2.5" />{item.price}
            </div>
          )}
        </div>
      </button>
    );
  }

  // chip layout (carousel)
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all hover:-translate-y-0.5"
      style={{ background: bg, color: accent, border: `1px solid ${border}` }}
      data-testid={`city-pulse-chip-${item.slot_id}`}
    >
      {isLive && (
        <span className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: '#06b6d4', animation: 'arenax-pulse-cyan 1.2s ease-in-out infinite' }} />
      )}
      <span className="text-sm">{item.emoji}</span>
      <span>{item.narrative.replace(`${item.emoji} `, '')}</span>
      <span className="opacity-60 mx-0.5">·</span>
      <span className="text-[10px] uppercase tracking-wider opacity-80">{item.when_label}</span>
      {item.is_peak && (
        <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded ml-0.5"
              style={{ background: 'rgba(217,119,6,0.18)', color: '#92400e' }}>
          PEAK
        </span>
      )}
    </button>
  );
}
